import { isLeft, isRight } from 'fp-ts/Either';
import { use, useState } from 'react';
import { toast } from 'sonner';
import { TRPCContext } from '../contexts/TRPC';
import type { DappDetail } from '../stores/dappsPageStore';
import { captureAndReportError } from '../utils/errorReporting';
import { getDappDeletionConstraintMessage } from '../utils/dappDeletion';

interface UseDappDeletionOptions {
  component: string;
  operation: string;
  onDeleted: () => Promise<void> | void;
}

export function useDappDeletion({
  component,
  operation,
  onDeleted,
}: UseDappDeletionOptions) {
  const trpc = use(TRPCContext);
  const [deleteModalDapp, setDeleteModalDapp] = useState<DappDetail | null>(
    null,
  );
  const [deleteReason, setDeleteReason] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  const closeDeleteDialog = () => {
    setDeleteModalDapp(null);
    setDeleteReason('');
  };

  const openDeleteDialog = (dapp: DappDetail) => {
    setDeleteModalDapp(dapp);
  };

  const confirmDelete = async () => {
    if (!trpc?.client || !deleteModalDapp) {
      return;
    }

    const deletionBlockedMessage = getDappDeletionConstraintMessage(
      deleteModalDapp.releaseCount,
    );

    if (deletionBlockedMessage) {
      toast.error(deletionBlockedMessage);
      return;
    }

    setIsDeleting(true);

    try {
      const result = await trpc.client.softDeleteDapp.mutate({
        dappId: deleteModalDapp.id,
        reason: deleteReason.trim() || undefined,
      });

      if (isRight(result)) {
        toast.success(result.right.message);
        closeDeleteDialog();
        try {
          await onDeleted();
        } catch (error) {
          captureAndReportError(error, {
            operation: `${operation}.reload`,
            component,
            dappId: deleteModalDapp.id,
          });
          toast.error('Dapp deleted, but failed to refresh the dapp list');
        }
      } else if (isLeft(result)) {
        toast.error(result.left.message);
      }
    } catch (error) {
      captureAndReportError(error, {
        operation,
        component,
        dappId: deleteModalDapp.id,
      });
      toast.error('Failed to delete dapp');
    } finally {
      setIsDeleting(false);
    }
  };

  return {
    deleteModalDapp,
    deleteReason,
    isDeleting,
    openDeleteDialog,
    closeDeleteDialog,
    setDeleteReason,
    confirmDelete,
  };
}
